import DatePill from "app/components/AdaptiveDatePicker/DatePill";
import Button from "app/components/Button";
import { Calendar } from "app/components/Calendar";
import { CalendarIcon, DropdownIcon } from "app/components/Icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "app/components/Popover";
import {
  getSafeFormattedDate,
  useDueDateFilter,
} from "app/containers/Features/containers/Home/hooks/useDueDateFilter";
import { FixedDateOptionsList } from "app/containers/Features/containers/Sprints/container/DateFilter/FixedDateOptionsList";
import clsx from "clsx";
import React from "react";

interface Props {
  selectedIds: string[];
  onUpdate: (values: string[]) => void;
  options?: string[];
}

const DueDateFilter: React.FC<Props> = ({ selectedIds, onUpdate }) => {
  const {
    open,
    setOpen,
    dateRange,
    setDateRange,
    activeKey,
    setActiveKey,
    handleRangeSelect,
    clearStartDate,
    clearEndDate,
    onSave,
    onCancel,
    getButtonLabel,
    isAllSelected,
    color,
  } = useDueDateFilter(selectedIds, onUpdate);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={clsx(
            "flex items-center gap-1 h-8 px-2.5 border rounded-full bg-white outline-none",
            isAllSelected
              ? "!border-primary"
              : open
                ? "border-secondary"
                : "border-secondary",
          )}
        >
          <CalendarIcon size={20} color={color} />
          <span
            className={clsx(
              "text-sm font-medium",
              isAllSelected ? "text-primary" : "text-secondary",
            )}
          >
            {getButtonLabel()}
          </span>
          <div
            className={clsx(
              "transition-transform duration-200 ease-in-out",
              open ? "rotate-180" : "rotate-0",
            )}
          >
            <DropdownIcon size={20} color={color} />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="min-w-[355px] px-0 py-2 bg-white border rounded-md"
      >
        <div className="flex items-center gap-2 px-4 h-[39px] border-b">
          <DatePill
            date={getSafeFormattedDate(dateRange.from)}
            onClear={clearStartDate}
            iconShow={!!dateRange.from}
            placeHolder="Start date"
            className="!p-0"
          />
          -
          <DatePill
            date={getSafeFormattedDate(dateRange.to)}
            onClear={clearEndDate}
            iconShow={!!dateRange.to}
            placeHolder="End date"
            className="!p-0"
          />
        </div>

        <div className="flex gap-1">
          <FixedDateOptionsList
            activeKey={activeKey}
            onSelect={handleRangeSelect}
            setActiveKey={setActiveKey}
          />

          <div className="p-2 w-full">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                setDateRange(range || { from: undefined, to: undefined });
                setActiveKey(null);
              }}
              required={false}
              className="w-full"
              monthGridClassName="w-full"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 border-t px-4 h-[45px]">
          <Button
            className="!bg-[transparent] flex items-center justify-center border border-secondary rounded !text-secondary h-[28px] text-xs font-medium "
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className=" flex items-center justify-center h-[28px] text-xs font-medium "
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DueDateFilter;
